/**
 * OwnerGatedControl — honest owner-gating for staff (WO-H1, invariant 6).
 *
 * Some actions (binding pricing, approve, send-for-signature) are OWNER-only and enforced on the
 * server. Rather than show a dead button, this wrapper — for non-owners — renders the control
 * disabled WITH a plain-language reason and a "request owner approval" path. For an owner it
 * renders the control untouched.
 *
 * It never fakes authority client-side: it does not perform the gated action, it only explains and
 * routes a request. The server remains the source of truth.
 *
 * Styling note: the gated action's own (copper) button is the single owner-authority control per
 * screen; the "request" affordance here is a secondary link, never copper.
 */
import React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text } from '../ui';
import { theme } from '../../theme';
import { useSession } from '../../auth/session';

export interface OwnerGatedControlProps {
  children: React.ReactNode;
  /** Plain-language why-it's-locked, e.g. "Only the owner can send a quote for signature." */
  reason: string;
  /** Label for the request action. */
  requestLabel?: string;
  /** Called when a non-owner taps "request". Wire to your notify/request flow; defaults to a no-op. */
  onRequest?: () => void;
  /** Hide the request affordance (explainer only). */
  hideRequest?: boolean;
}

export function OwnerGatedControl({
  children,
  reason,
  requestLabel = 'Request owner approval',
  onRequest,
  hideRequest,
}: OwnerGatedControlProps) {
  const { user } = useSession();
  const isOwner = user?.role === 'owner';

  if (isOwner) return <>{children}</>;

  return (
    <View style={styles.wrap} accessibilityLabel={`Owner only. ${reason}`}>
      <View style={styles.disabled} pointerEvents="none">
        {children}
      </View>
      <Text variant="small" color="secondary">
        {reason}
      </Text>
      {!hideRequest && (
        <Pressable
          onPress={onRequest}
          accessibilityRole="button"
          accessibilityLabel={requestLabel}
          disabled={!onRequest}
          style={styles.request}
        >
          <Text variant="bodyStrong" color={onRequest ? 'link' : 'secondary'}>
            {requestLabel}
          </Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: theme.spacing.xs },
  disabled: { opacity: 0.45 },
  request: { minHeight: theme.layout.minTapTarget, justifyContent: 'center' },
});
